import { createApp } from "vue";
import { LoadingBar, Quasar } from "quasar";
import ElementPlus from "element-plus";
import VueGtag from "vue-gtag";
import { H } from "highlight.run";
import i18n from "@/plugins/i18n";
import VueHtmlToPaper from "@/plugins/VueHtmlToPaper";
import { initializeSentry } from "@/plugins/sentry.js";
import store from "@/store/index.js";
import { isProduction } from "@/utils/env";
import theme from "@/scss/_theme.js";

import "quasar/dist/quasar.css";
import "@quasar/extras/line-awesome/line-awesome.css";
import "@quasar/extras/material-icons/material-icons.css";
import "@/scss/element-theme.scss";
import "@/scss/style.scss";

import App from "./App.vue";
import router from "./router";
import { authService } from "@/services/authService.js";

H.init("neyxrjd4", {
  enableCanvasRecording: true,
  samplingStrategy: {
    canvasManualSnapshot: 2,
    canvasMaxSnapshotDimension: 480,
  },
});

const initializeApp = async () => {
  authService.oidcAuth.useRouter(router);

  const ok = await authService.oidcAuth.startup();

  if (ok) {
    if (authService.oidcAuth.isAuthenticated) {
      try {
        const cachedUser = authService.oidcAuth.user;
        const cachedSub = cachedUser?.profile?.sub;
        const response = await fetch(
          `${window.runtimeConfig.ZITADEL_ISSUER}/oidc/v1/userinfo`,
          {
            headers: {
              Authorization: `Bearer ${authService.accessToken}`,
            },
            credentials: 'include',
          }
        );

        if (!response.ok) {
          throw new Error('Session invalid');
        }
        const userInfo = await response.json();

        if (userInfo.sub !== cachedSub) {
          await authService.oidcAuth.mgr.removeUser();
          window.location.reload();
          return; // Stop initialization
        }
      } catch (error) {
        await authService.oidcAuth.mgr.removeUser();
      }
    }

    const app = createApp(App);

    app
      .use(store)
      .use(router)
      .use(Quasar, {
        config: {
          brand: {
            // Colors from @/scss/_theme.js
            primary: theme.primary,
            secondary: theme.secondary,
            accent: theme.accent,
            dark: theme.dark,
            positive: theme.positive,
            negative: theme.negative,
            info: theme.info,
            warning: theme.warning,
          },
          loadingBar: {
            color: "primary",
            hijackFilter: () => false,
          },
        },
        plugins: {
          LoadingBar,
        },
      })
      .use(i18n)
      .use(ElementPlus)
      .use(VueHtmlToPaper)
      .use(VueGtag, {
        config: {
          id: "G-706HJQFY6N",
          params: {
              server_container_url: "https://gtm.airteam.cloud",
            },
        },
      })
      .mount("#app");

    if (isProduction) {
      initializeSentry(app);
    }
  }
};

initializeApp().catch(error => {
  console.error('Failed to initialize app:', error);
});
